import {
  RFPDocumentSummary,
  RFPDocumentSummaryResponseBody,
  RFPDocumentSummaryResponseRecord,
  RFPSortBy,
  SiteRFPPhaseAPIResponse,
} from '@black-bear-energy/black-bear-energy-common'
import { AxiosInstance } from 'axios'
import {
  useQuery,
  UseQueryResult,
  keepPreviousData,
} from '@tanstack/react-query'
import { useApiWithIDTokenGranted } from '../../state/modules/common'
import { transformSiteRFPDocumentSummary } from './helpers/transformSiteRFPDocumentSummary'

/**
 * Fetch paginated upload summaries for each site RFP, grouped by customer RFP.
 * @param search - The search term to filter by.
 * This can be a project name, a site name, or an address.
 * @param requiredMissing - Whether to filter for sites with required documents missing.
 * @param reviewMissing - Whether to filter for sites with documents that need review.
 * @param phase - The site RFP phases to filter by.
 * @param sortBy - The field to sort by.
 * @param sortAsc - If false, the results will be sorted in descending order.
 * @param page - The page number to fetch, starting at 1.
 * @returns A UseQueryResult object containing the RFP document summaries and pagination information.
 */
export const useRFPDocumentSummaries = (
  search: string = '',
  requiredMissing: boolean = false,
  reviewMissing: boolean = false,
  phase: SiteRFPPhaseAPIResponse[] = [],
  sortBy: RFPSortBy = RFPSortBy.RFPName,
  sortAsc: boolean = true,
  page: number = 1
): UseQueryResult<RFPDocumentSummariesResult, Error> => {
  const api = useApiWithIDTokenGranted()

  return useQuery({
    queryKey: [
      api,
      'rfpDocumentSummaries',
      search,
      requiredMissing,
      reviewMissing,
      phase,
      sortBy,
      sortAsc,
      page,
    ],
    queryFn: () =>
      fetchRFPDocumentSummaries(
        api as AxiosInstance,
        search,
        requiredMissing,
        reviewMissing,
        phase,
        sortBy,
        sortAsc,
        page
      ),
    select: transformRFPDocumentSummaries,
    placeholderData: keepPreviousData,
    enabled: !!api,
  })
}

/** The result from the useRFPDocumentSummaries hook. */
export type RFPDocumentSummariesResult = {
  records: RFPDocumentSummary[]
  pagination: RFPDocumentSummaryResponseBody['pagination']
}

async function fetchRFPDocumentSummaries(
  api: AxiosInstance,
  search: string,
  requiredMissing: boolean,
  reviewMissing: boolean,
  phase: SiteRFPPhaseAPIResponse[],
  sortBy: RFPSortBy,
  sortAsc: boolean,
  page: number
): Promise<RFPDocumentSummaryResponseBody> {
  const response = await api.get<RFPDocumentSummaryResponseBody>(
    '/rfp-document-summaries',
    {
      params: {
        search,
        requiredMissing,
        reviewMissing,
        phase,
        sortBy,
        sortAsc,
        page,
      },
    }
  )
  return response.data
}

const transformRFPDocumentSummaries = (
  data: RFPDocumentSummaryResponseBody
): {
  records: RFPDocumentSummary[]
  pagination: RFPDocumentSummaryResponseBody['pagination']
} => {
  const records = data.records.map((responseElement) =>
    transformRFPDocumentSummary(responseElement)
  )
  return {
    records,
    pagination: data.pagination,
  }
}

const transformRFPDocumentSummary = (
  responseElement: RFPDocumentSummaryResponseRecord
): RFPDocumentSummary => {
  const rfpDocumentSummary: RFPDocumentSummary = {
    ...responseElement,
    lastUpdated: new Date(responseElement.lastUpdated),
    siteRFPs: responseElement.siteRFPs.map((siteRFP) =>
      transformSiteRFPDocumentSummary(siteRFP)
    ),
  }
  return rfpDocumentSummary
}
